import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import { useFragment, type FragmentType } from "~/gql/runtime";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { Link } from "~/features/ui/link";
import { Asset, type fragment as assetFragment } from "~/features/craft/asset";
import { fragment as linkFragment, isLinkValid } from "~/features/ui/link/utils";
export type ExtraNavLogoLayout = "app" | "landing";
type Props = {
  layout: ExtraNavLogoLayout;
  extraNavLogo?: FragmentType<typeof assetFragment>;
  extraNavLogoTransparent?: FragmentType<typeof assetFragment>;
  extraNavLogoLink?: FragmentType<typeof linkFragment>;
};
const chrome = {
  app: {
    transparent: {
      "pointerEvents": "auto",
      "display": "flex",
      "height": "var(--header-height)",
      "flexShrink": "0",
      "alignItems": "center",
      "borderBottomWidth": "1px",
      "borderRightWidth": "1px",
      "borderColor": "rgba(255, 255, 255, 0.2)",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem"
    },
    solid: {
      "pointerEvents": "auto",
      "display": "flex",
      "height": "var(--header-height)",
      "flexShrink": "0",
      "alignItems": "center",
      "borderBottomWidth": "1px",
      "borderRightWidth": "1px",
      "borderColor": "rgb(0 7 4 / 0.1)",
      "--tw-bg-opacity": "1",
      "backgroundColor": "rgb(244 244 249 / var(--tw-bg-opacity))",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem"
    },
    highlighted: {
      "pointerEvents": "auto",
      "display": "flex",
      "height": "var(--header-height)",
      "flexShrink": "0",
      "alignItems": "center",
      "borderBottomWidth": "1px",
      "borderRightWidth": "1px",
      "borderColor": "rgb(0 7 4 / 0.1)",
      "backgroundColor": "var(--white-static)",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem"
    }
  },
  landing: {
    transparent: {
      "pointerEvents": "auto",
      "display": "flex",
      "height": "var(--header-height)",
      "flexShrink": "0",
      "alignItems": "center",
      "borderBottomWidth": "1px",
      "borderRightWidth": "1px",
      "borderColor": "rgba(255, 255, 255, 0.2)",
      "backgroundColor": "rgba(0, 7, 4, 0.3)",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem"
    },
    solid: {
      "pointerEvents": "auto",
      "display": "flex",
      "height": "var(--header-height)",
      "flexShrink": "0",
      "alignItems": "center",
      "borderBottomWidth": "1px",
      "borderRightWidth": "1px",
      "borderColor": "rgb(0 7 4 / 0.1)",
      "--tw-bg-opacity": "1",
      "backgroundColor": "rgb(244 244 249 / var(--tw-bg-opacity))",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem"
    },
    highlighted: {
      "pointerEvents": "auto",
      "display": "flex",
      "height": "var(--header-height)",
      "flexShrink": "0",
      "alignItems": "center",
      "borderBottomWidth": "1px",
      "borderRightWidth": "1px",
      "borderColor": "rgb(0 7 4 / 0.1)",
      "backgroundColor": "var(--white-static)",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem"
    }
  }
} as const;
const EXTRA_NAV_LOGO_ARIA_LABEL = "More information about Rev8";
function ExtraNavLogoAsset({
  asset
}: {
  asset?: FragmentType<typeof assetFragment>;
}) {
  if (!asset) return null;
  return <_StyledDiv>
      <_StyledAsset asset={asset} imageProps={{
      rounded: false,
      fit: "contain",
      priority: true
    }} />
    </_StyledDiv>;
}
function ExtraNavLogoWithLink(props: {
  layout: ExtraNavLogoLayout;
  asset?: FragmentType<typeof assetFragment>;
  link: FragmentType<typeof linkFragment>;
  surface: keyof (typeof chrome)["app"];
  isPageHighlighted: boolean;
}) {
  const {
    layout,
    asset,
    link,
    surface,
    isPageHighlighted
  } = props;
  const linkData = useFragment(linkFragment, link);
  const styles = [chrome[layout][surface], isPageHighlighted && {
    "backgroundColor": "var(--white-static)"
  }];
  if (isLinkValid(linkData)) {
    return <_StyledLink {...link} aria-label={EXTRA_NAV_LOGO_ARIA_LABEL} $_css={[styles, {
      "flex": "1 1 0%",
      "@media (hover: hover)": {
        "&:hover > div": {
          "opacity": "0.8"
        }
      }
    }]}>
        <ExtraNavLogoAsset asset={asset} />
      </_StyledLink>;
  }
  return <_StyledDiv2 $_css2={[styles, {
    "flex": "1 1 0%"
  }]}>
      <ExtraNavLogoAsset asset={asset} />
    </_StyledDiv2>;
}
export function ExtraNavLogo(props: Props) {
  const {
    layout,
    extraNavLogo,
    extraNavLogoTransparent,
    extraNavLogoLink
  } = props;
  const {
    navTheme,
    isPageHighlighted
  } = useNavLayout();
  const surface = isPageHighlighted ? "highlighted" : navTheme;
  const resolvedAsset = surface === "transparent" ? extraNavLogoTransparent ?? extraNavLogo : extraNavLogo;
  if (extraNavLogoLink) {
    return <ExtraNavLogoWithLink layout={layout} asset={resolvedAsset} link={extraNavLogoLink} surface={surface} isPageHighlighted={isPageHighlighted} />;
  }
  return <_StyledDiv3 $_css3={[chrome[layout][surface], isPageHighlighted && {
    "backgroundColor": "var(--white-static)"
  }, {
    "flex": "1 1 0%"
  }]}>
      <ExtraNavLogoAsset asset={resolvedAsset} />
    </_StyledDiv3>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-1up982h-0"
})({
  "maxHeight": "4rem",
  "width": "auto",
  "maxWidth": "140px",
  "transitionProperty": "opacity",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out"
});
var _StyledAsset = _styled2(Asset).withConfig({
  componentId: "sc-sf-1up982h-1"
})({
  "height": "auto",
  "width": "100%"
});
var _StyledLink = _styled3(Link).withConfig({
  componentId: "sc-sf-1up982h-2"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-1up982h-3"
})`${p => p.$_css2}`;
var _StyledDiv3 = /*#__PURE__*/_styled5("div").withConfig({
  displayName: "extra-nav-logo___StyledDiv3",
  componentId: "sc-sz6dmw-0"
})(["", ""], p => p.$_css3);